import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sun, Sunset, Moon, Lightbulb, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioCardGroup } from '@/components/ui/radio-card-group';
import { ApiError, createRender, uploadInput } from '@/lib/api';
import { useSession } from '@/lib/session';
import { useToast } from '@/components/Toaster';
import { TIERS } from '@archi/prompts';
import type {
  LightTempId,
  RenderTierId,
  SceneId,
  TimeOfDayId,
} from '@archi/types';

const SCENE_OPTIONS: ReadonlyArray<{ value: SceneId; label: string }> = [
  { value: 'fachada_residencial', label: 'Fachada residencial' },
  { value: 'interior_sala', label: 'Sala' },
  { value: 'interior_cozinha', label: 'Cozinha' },
  { value: 'interior_quarto', label: 'Quarto' },
  { value: 'interior_banheiro', label: 'Banheiro' },
  { value: 'escritorio_comercial', label: 'Escritório' },
  { value: 'area_externa', label: 'Área externa' },
  { value: 'generico', label: 'Genérico' },
];

const TOD_OPTIONS = [
  { value: 'meio_dia' as TimeOfDayId, label: 'Meio-dia', icon: <Sun className="size-4" /> },
  { value: 'entardecer' as TimeOfDayId, label: 'Entardecer', icon: <Sunset className="size-4" /> },
  { value: 'noite' as TimeOfDayId, label: 'Noite', icon: <Moon className="size-4" /> },
  { value: 'interior_cego' as TimeOfDayId, label: 'Interior sem janelas', icon: <Lightbulb className="size-4" /> },
];

const LIGHT_OPTIONS = [
  { value: 'none' as LightTempId, label: 'Automático', description: 'Mantém iluminação natural da cena' },
  { value: 'warm_3000k' as LightTempId, label: '3.000 K', description: 'Aconchego' },
  { value: 'neutral_5000k' as LightTempId, label: '5.000 K', description: 'Foco neutro' },
  { value: 'cool_8000k' as LightTempId, label: '8.000 K', description: 'Conceitual frio' },
];

const TIER_OPTIONS: ReadonlyArray<{
  value: RenderTierId;
  label: string;
  description: string;
}> = [
  { value: 'draft', label: `Draft · ${TIERS.draft.creditsCharged} créd.`, description: TIERS.draft.tagline },
  { value: 'standard', label: `Standard · ${TIERS.standard.creditsCharged} créd.`, description: TIERS.standard.tagline },
  { value: 'premium', label: `Premium Wide · ${TIERS.premium.creditsCharged} créd.`, description: TIERS.premium.tagline },
  { value: 'variants', label: `Variants x3 · ${TIERS.variants.creditsCharged} créd.`, description: TIERS.variants.tagline },
];

// Biblioteca de sugestões por categoria — chips clicáveis.
const SUGGESTION_CATEGORIES: ReadonlyArray<{ label: string; items: string[] }> = [
  {
    label: 'Madeiras',
    items: ['cumaru natural', 'carvalho oak', 'ipê escuro', 'madeira de demolição', 'pau-ferro', 'freijó claro', 'ripado vertical'],
  },
  {
    label: 'Pedras & cimentícios',
    items: ['travertino romano', 'mármore carrara', 'granito preto são gabriel', 'concreto aparente', 'cimento queimado', 'pedra hijau', 'porcelanato bege'],
  },
  {
    label: 'Tecidos & estofados',
    items: ['linho cru', 'bouclé off-white', 'veludo verde-oliva', 'ratã natural', 'couro caramelo', 'algodão chambray', 'lã trama grossa'],
  },
  {
    label: 'Luz & atmosfera',
    items: ['golden hour pela direita', 'luz volumétrica com partículas', 'contraluz suave', 'LED linear teto', 'pendentes cerâmicos', 'arandela parede', 'sombras dramáticas'],
  },
  {
    label: 'Paisagismo tropical',
    items: ['costela-de-adão', 'areca palm', 'zamioculcas', 'strelitzia', 'bromélias', 'muxarabi de ipê', 'jardim vertical'],
  },
  {
    label: 'Estilo de referência',
    items: ['modernismo brasileiro', 'minimal contemporâneo', 'editorial casa vogue', 'tropical refinado', 'tropical brutalismo', 'boho clean', 'industrial aconchegante'],
  },
];

export const UploadPage = () => {
  const uid = useSession((s) => s.session?.user.id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [file, setFile] = React.useState<File | null>(null);
  const [tier, setTier] = React.useState<RenderTierId>('standard');
  const [scene, setScene] = React.useState<SceneId>('interior_sala');
  const [tod, setTod] = React.useState<TimeOfDayId>('entardecer');
  const [light, setLight] = React.useState<LightTempId>('none');
  const [note, setNote] = React.useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file || !uid) throw new Error('faltando imagem ou sessão');
      const inputKey = await uploadInput(uid, file);
      return createRender({
        input_key: inputKey,
        tier,
        scene,
        time_of_day: tod,
        light_temp: light === 'none' ? undefined : light,
        user_note: note.trim() || undefined,
      });
    },
    onSuccess: ({ job_id }) => {
      toast({ title: 'Render enfileirado', description: 'Vou te avisar quando ficar pronto.', variant: 'success' });
      navigate(`/renders/${job_id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.body.code === 'INSUFFICIENT_CREDITS') {
        toast({ title: 'Créditos insuficientes', description: 'Adicione créditos para continuar.', variant: 'destructive' });
        navigate('/creditos');
        return;
      }
      toast({ title: 'Falha ao gerar render', description: (err as Error).message, variant: 'destructive' });
    },
  });

  const onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) setFile(f);
  };

  const preview = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const toggleChip = React.useCallback((item: string) => {
    setNote((n) => {
      const active = n.toLowerCase().includes(item.toLowerCase());
      if (active) {
        const re = new RegExp(`,?\\s*${item.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}`, 'i');
        return n.replace(re, '').replace(/^,\s*/, '').trim();
      }
      const next = n.trim() ? `${n.trim()}, ${item}` : item;
      return next.slice(0, 500);
    });
  }, []);

  const disabled = !file || mutation.isPending;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6 lg:p-10">
      <header className="space-y-2">
        <h1 className="font-display text-3xl">Transforme sua imagem</h1>
        <p className="text-muted-foreground">
          Upload da imagem → escolha os parâmetros → descreva sua cena → gere.
          A IA respeita ângulos e proporções da imagem original.
        </p>
      </header>

      {/* ───────────────────── 1. Imagem ───────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>1. Imagem de entrada</CardTitle>
          <CardDescription>JPG, PNG ou WebP até 50 MB. Aceita SketchUp, Revit, sketch, foto de obra vazia.</CardDescription>
        </CardHeader>
        <CardContent>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="grid aspect-[4/3] cursor-pointer place-items-center rounded-lg border-2 border-dashed border-input bg-muted/30 p-6 text-center transition-colors hover:border-primary/50"
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
              }}
              data-testid="input-file"
            />
            {preview ? (
              <img src={preview} alt="pré-visualização" className="max-h-[50vh] rounded-md object-contain" />
            ) : (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Arraste a imagem aqui</p>
                <p>ou clique para selecionar</p>
              </div>
            )}
          </label>
        </CardContent>
      </Card>

      {/* ───────────────────── 2. Cena ───────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>2. Cena e iluminação</CardTitle>
          <CardDescription>Parâmetros básicos. Você pode trocar a qualquer momento antes de gerar.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Tipo de ambiente</Label>
            <RadioCardGroup
              name="scene"
              columns={4}
              value={scene}
              onValueChange={setScene}
              options={SCENE_OPTIONS}
            />
          </div>
          <div className="space-y-2">
            <Label>Hora do dia</Label>
            <RadioCardGroup
              name="tod"
              columns={4}
              value={tod}
              onValueChange={setTod}
              options={TOD_OPTIONS}
            />
          </div>
          <div className="space-y-2">
            <Label>Temperatura da iluminação (opcional)</Label>
            <RadioCardGroup
              name="light"
              columns={4}
              value={light}
              onValueChange={setLight}
              options={LIGHT_OPTIONS}
            />
          </div>
        </CardContent>
      </Card>

      {/* ───────────────────── 3. DESCRIÇÃO — DESTACADO ───────────────────── */}
      <Card className="border-primary/40 bg-accent/20 ring-1 ring-primary/20">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="mt-1 flex size-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="size-5" />
            </div>
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                3. Descreva sua cena
                <span className="rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-widest text-primary">
                  Opcional · recomendado
                </span>
              </CardTitle>
              <CardDescription>
                Conte o que você quer em <strong>materiais</strong>, <strong>texturas</strong>, <strong>iluminação</strong> e <strong>paisagismo</strong>.
                Quanto mais específico, mais fiel o render. Deixar em branco também funciona — usamos valores padrão.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Textarea grande, protagonista */}
          <div className="space-y-2">
            <Textarea
              id="note"
              placeholder="Ex: parede de cimento queimado cinza claro, sofá bouclé off-white, mesa redonda de travertino, ripado vertical em carvalho, paisagismo com costela-de-adão e areca palm, luz dourada entrando pela direita, pendentes cerâmicos acima da mesa…"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={6}
              className="resize-y bg-background text-base leading-relaxed"
              data-testid="input-note"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Escreva em português mesmo. A IA entende perfeitamente.</span>
              <span className="font-mono">{note.length}/500</span>
            </div>
          </div>

          {/* Divider */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/60" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-accent/20 px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Ou escolha das sugestões
              </span>
            </div>
          </div>

          {/* Chips de sugestão */}
          <div className="space-y-4">
            {SUGGESTION_CATEGORIES.map((cat) => (
              <div key={cat.label}>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {cat.label}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.items.map((item) => {
                    const active = note.toLowerCase().includes(item.toLowerCase());
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleChip(item)}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background/70 text-foreground hover:border-foreground hover:bg-background'
                        }`}
                      >
                        {active && <span className="mr-1">✓</span>}
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ───────────────────── 4. Qualidade ───────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>4. Qualidade</CardTitle>
          <CardDescription>
            Draft é rascunho rápido. Standard é o equilíbrio (alta resolução 16:9).
            Premium é cinematic 21:9. Variants gera 3 opções pra você escolher.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioCardGroup
            name="tier"
            columns={2}
            value={tier}
            onValueChange={setTier}
            options={TIER_OPTIONS}
          />
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          size="lg"
          disabled={disabled}
          onClick={() => mutation.mutate()}
          data-testid="generate-btn"
        >
          {mutation.isPending
            ? 'Gerando…'
            : `Gerar (${TIERS[tier].creditsCharged} ${TIERS[tier].creditsCharged === 1 ? 'crédito' : 'créditos'})`}
        </Button>
      </div>
    </div>
  );
};
